import { useMemo } from "react";
import { geographyText, useLocale } from "@ncp/i18n";
import type { MarketGraph, GraphNode } from "./api";

const palette = { ink: "#1B2A31", muted: "#5E6B72", faint: "#9AA5AB", gold: "#B58D2E", petrol: "#07597A", navy: "#063E55", cream: "#F5F1E8", rule: "#E3DCCB" } as const;

type Layer = "Principal" | "Owner" | "Undertaking" | "Market";
const layerOf = (n: GraphNode): Layer => (n.kind === "Principal" ? "Principal" : n.kind === "Undertaking" ? "Undertaking" : n.kind === "Market" ? "Market" : "Owner");
const ARABIC = /[\u0600-\u06FF]/;
/** The Arabic label where one is recorded; a name kept in its registered English form is marked as English. */
const pick = (en: string, ar: string | null | undefined, rtl: boolean) => (rtl && ar && ARABIC.test(ar) ? ar : en);
const langOf = (s: string, rtl: boolean) => (rtl && /[A-Za-z]/.test(s) && !ARABIC.test(s) ? "en" : undefined);
const trunc = (s: string, n: number) => (s.length <= n ? s : `${s.slice(0, Math.max(1, n - 1))}…`);

/** Breaks a label at spaces into at most two lines of roughly `perLine` characters; the second line is truncated. */
function wrap(text: string, perLine: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const w of words) {
    if (!current) current = w;
    else if (current.length + 1 + w.length <= perLine) current += ` ${w}`;
    else { lines.push(current); current = w; if (lines.length === 1) break; }
  }
  if (current) lines.push(current);
  const consumed = lines.join(" ");
  const out = lines.slice(0, 2);
  if (out.length && (consumed.length < text.length || out[out.length - 1]!.length > perLine)) out[out.length - 1] = trunc(out[out.length - 1]!, perLine);
  return out;
}

/**
 * The ownership, directorship and agency graph of a market, laid out in layers: principals on top, owners and persons next, the undertakings,
 * and the market at the bottom. Solid petrol edges are shareholdings, dotted navy edges directorships, dashed gold edges registered exclusive
 * agencies, faint edges the share each undertaking holds. Enough to make a hidden common owner visible at a glance.
 */
export function MarketGraphSvg({ graph, width = 900 }: { graph: MarketGraph; width?: number }) {
  const layout = useMemo(() => {
    const order: Layer[] = ["Principal", "Owner", "Undertaking", "Market"];
    const layers = order.map((l) => graph.nodes.filter((n) => layerOf(n) === l)).filter((l) => l.length > 0);
    const layerH = 124;
    const top = 32;
    const height = top * 2 + layerH * Math.max(1, layers.length - 1) + 48;
    const pos = new Map<string, { x: number; y: number; w: number; node: GraphNode }>();
    layers.forEach((layer, li) => {
      const slot = width / layer.length;
      layer.forEach((n, i) => pos.set(n.id, { x: slot * i + slot / 2, y: top + li * layerH, w: Math.min(slot - 10, n.kind === "Market" ? 420 : 168), node: n }));
    });
    return { pos, height };
  }, [graph, width]);

  const rtl = useLocale().locale === "ar";
  let labelled = 0;
  return (
    <svg viewBox={`0 0 ${width} ${layout.height}`} className="w-full" role="img" aria-label={graph.market}>
      {graph.edges.map((e, i) => {
        const a = layout.pos.get(e.from);
        const b = layout.pos.get(e.to);
        if (!a || !b) return null;
        const style = e.kind === "Agency" ? { stroke: palette.gold, dash: "7 5", w: 1.8 } : e.kind === "Share" ? { stroke: palette.faint, dash: undefined, w: 1 } : e.kind === "Director" || e.kind === "Manager" ? { stroke: palette.navy, dash: "2 4", w: 1.5 } : { stroke: palette.petrol, dash: undefined, w: 2 };
        const y1 = a.y + 22;
        const y2 = b.y - 22;
        const showLabel = e.kind !== "Share" && e.kind !== "Agency" && !!e.label;
        const f = showLabel ? 0.3 + 0.2 * (labelled++ % 3) : 0.5;
        const mx = a.x + (b.x - a.x) * f;
        const my = y1 + (y2 - y1) * f;
        const label = showLabel ? trunc(pick(e.label!, e.labelAr, rtl), 24) : null;
        return (
          <g key={`${e.from}-${e.to}-${i}`}>
            <line x1={a.x} y1={y1} x2={b.x} y2={y2} stroke={style.stroke} strokeWidth={style.w} strokeDasharray={style.dash} />
            {label ? (
              <g>
                <rect x={mx - label.length * 3.1 - 5} y={my - 8} width={label.length * 6.2 + 10} height={15} rx={4} fill="#FFFDF8" stroke={palette.rule} strokeWidth={0.6} />
                <text x={mx} y={my + 3} textAnchor="middle" fontSize={8.5} fill={palette.navy} lang={langOf(label, rtl)}>{label}</text>
              </g>
            ) : null}
          </g>
        );
      })}
      {graph.nodes.map((n) => {
        const p = layout.pos.get(n.id);
        if (!p) return null;
        const style = n.kind === "Principal" ? { fill: palette.cream, stroke: palette.gold, text: "#8A6B22" } : n.kind === "Person" ? { fill: "#FFFDF8", stroke: palette.navy, text: palette.navy } : n.kind === "Undertaking" ? { fill: palette.petrol, stroke: palette.petrol, text: "#FFFFFF" } : n.kind === "Market" ? { fill: palette.navy, stroke: palette.navy, text: "#FFFFFF" } : { fill: "#EAF2F5", stroke: palette.petrol, text: palette.navy };
        const fontSize = n.kind === "Market" ? 10.5 : 9;
        const perLine = Math.max(6, Math.floor((p.w - 12) / (fontSize * 0.54)));
        const lines = wrap(pick(n.label, n.labelAr, rtl), perLine);
        const place = n.emirate ? geographyText(n.emirate, rtl ? "ar" : "en") : "";
        const sub = n.kind === "Undertaking" && n.sharePercent != null ? `${n.sharePercent}% · ${place}` : n.kind === "Principal" ? place || undefined : undefined;
        const h = 44;
        const startY = p.y - (lines.length - 1) * 5 - (sub ? 4.5 : 0) + 3.5;
        return (
          <g key={n.id}>
            <rect x={p.x - p.w / 2} y={p.y - h / 2} width={p.w} height={h} rx={n.kind === "Person" ? 22 : 8} fill={style.fill} stroke={style.stroke} strokeWidth={1.2} />
            {lines.map((line, li) => <text key={li} x={p.x} y={startY + li * 10.5} textAnchor="middle" fontSize={fontSize} fontWeight={700} fill={style.text} lang={langOf(line, rtl)}>{line}</text>)}
            {sub ? <text x={p.x} y={startY + lines.length * 10.5 + 1} textAnchor="middle" fontSize={8} fill={style.text} fillOpacity={0.85} lang={langOf(sub, rtl)}>{trunc(sub, perLine + 4)}</text> : null}
          </g>
        );
      })}
    </svg>
  );
}
